import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const FuturisticButton = ({ 
  children, 
  icon, 
  variant = 'primary', 
  size = 'md', 
  loading = false, 
  className = '', 
  as: Component = 'button',
  ...props 
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white font-bold shadow-lg shadow-green-500/30 hover:shadow-green-400/40',
    secondary: 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-green-300 font-bold shadow-lg shadow-gray-800/30',
    outline: 'border-2 border-green-400/80 hover:border-green-300 bg-transparent hover:bg-green-400/20 text-green-300 hover:text-green-200 font-bold shadow-lg shadow-green-400/20',
    glass: 'backdrop-blur-xl bg-black/70 hover:bg-black/80 border-2 border-green-400/50 hover:border-green-300/70 text-green-300 hover:text-green-200 font-bold shadow-lg shadow-green-400/30',
    danger: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold shadow-lg shadow-red-500/30'
  };

  const sizes = {
    sm: 'px-3 py-2 text-xs sm:text-sm min-h-[36px] sm:min-h-[40px]',
    md: 'px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base min-h-[40px] sm:min-h-[44px]',
    lg: 'px-4 py-3 sm:px-6 sm:py-4 text-sm sm:text-lg min-h-[44px] sm:min-h-[52px]',
    xl: 'px-4 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 text-sm sm:text-lg md:text-xl min-h-[48px] sm:min-h-[52px] md:min-h-[60px]'
  };

  return (
    <motion.div 
      className="relative inline-block w-full"
      whileHover={{ scale: 1.02, y: -1 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      <Component
        className={`
          relative overflow-hidden rounded-xl font-bold transition-all duration-300 ease-out 
          hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-green-400/50 
          disabled:opacity-50 disabled:cursor-not-allowed matrix-button w-full 
          flex items-center justify-center gap-2 touch-manipulation
          ${variants[variant]} 
          ${sizes[size]} 
          ${className}
        `}
        {...props}
      >
        {/* Animated background shimmer */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-1000" />
        
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-green-400/20 to-emerald-400/20 rounded-xl blur opacity-0 hover:opacity-100 transition-opacity duration-300" />
        
        {/* Content */}
        <span className="relative z-10 flex items-center justify-center gap-1 sm:gap-2 font-bold text-shadow-sm w-full">
          {loading ? (
            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : icon ? (
            <SafeIcon icon={icon} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          ) : null}
          <span className="text-white font-bold text-shadow-sm truncate min-w-0 flex-1 text-center">
            {children}
          </span>
        </span>

        {/* Scan line effect */}
        <motion.div
          className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-green-300/80 to-transparent"
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3, ease: 'linear' }}
        />
      </Component>
    </motion.div>
  );
};

export default FuturisticButton;